import DOMPurify from 'dompurify'
import type {
  PlaceholderMap,
  buildIODStatementParams
} from './impact-of-disability_types'

export function populatePlaceholderText(text: string, placeholders: PlaceholderMap): string {
  let populatedText = text
  Object.entries(placeholders).forEach(([key, value]) => {
    if (value !== undefined) {
      const regex = new RegExp(`\\[${key}\\]`, 'g')
      populatedText = populatedText.replace(regex, value)
    }
  })
  return populatedText
}

/**
 * Statement builder that works directly with LLM-generated
 * statements instead of Airtable impact area intros.
 *
 * Expects statementsForDisability to be an array of objects with:
 * { subject, area_uuid, statement_text }
 */
export function buildIODStatement({
  statementsForDisability,
  selectedSubjects,
  selectedImpactAreas
}: buildIODStatementParams): [string, boolean] {
  const hasSelectedSubjects = selectedSubjects && selectedSubjects.length > 0
  const hasSelectedImpactAreas = selectedImpactAreas && selectedImpactAreas.length > 0

  let statement = ''
  let isStatementEmptyPlaceholder = false

  if (!hasSelectedImpactAreas) {
    const hasLLMStatements = (statementsForDisability || []).length > 0

    // If there are no impact areas configured but we DO have statements for the selected subjects
    if (hasSelectedSubjects && hasLLMStatements) {
      const filteredBySubject = statementsForDisability.filter(s =>
        selectedSubjects.includes(s.subject)
      )

      const statements = filteredBySubject.map(s => s.statement_text)
      statement = statements.map(s => `<p>${DOMPurify.sanitize(s)}</p>`).join('')
      return [populatePlaceholderText(statement, { studentName: '(student)' }), isStatementEmptyPlaceholder]
    }

    if (hasSelectedSubjects && !hasLLMStatements) {
      statement = '[Select an impacted area to compose a statement.]'
    } else if (!hasSelectedSubjects) {
      statement = '[Select a subject and an impacted area to compose a statement.]'
    }
    isStatementEmptyPlaceholder = true
    return [populatePlaceholderText(statement, { studentName: '(student)' }), isStatementEmptyPlaceholder]
  }

  if (!hasSelectedSubjects) {
    statement = '[Select a subject and an impacted area to compose a statement.]'
    isStatementEmptyPlaceholder = true
    return [populatePlaceholderText(statement, { studentName: '(student)' }), isStatementEmptyPlaceholder]
  }

  const selectedAreaUUIDs = selectedImpactAreas.map(ia => ia.area_uuid)

  const filteredStatements = (statementsForDisability || []).filter(s => {
    return selectedSubjects.includes(s.subject) && selectedAreaUUIDs.includes(s.area_uuid)
  })

  const statements = filteredStatements.map(s => s.statement_text)
  statement = statements.map(s => `<p>${DOMPurify.sanitize(s)}</p>`).join('')

  return [populatePlaceholderText(statement, { studentName: '(student)' }), isStatementEmptyPlaceholder]
}
